import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { SPORT_DISPLAY_NAMES } from '../config/sports'
import SubjectImage from '../components/SubjectImage'
import {
  SPORT_AVAILABLE_MARKETS, LEAGUE_TO_GROUP, VIRTUAL_LEAGUES, isVirtualLeague,
  ODDS_HIERARCHY, leagueLabel, buildMarketTree, orderPeriods, playersAreCompetitors,
  isFuturesLeague, futuresSportId, futuresToGames, marketKeysFor, roundLabel,
} from './oddsTaxonomy'
import { BOOKMAKERS_BY_REGION as BOOKMAKERS, ALL_BOOKMAKERS, BOOKMAKER_LOGOS } from '../config/bookmakers'
import { adjustPrice, isExchange, EXCHANGE_FEE_NOTE } from '../config/exchanges'
import '../styles/app.css'
import PreviewWall from '../components/PreviewWall'
import OddsBetslip, { slipKey } from './OddsBetslip'
import { oppFromOutcome } from './trackFromOdds'
import useVisiblePolling from '../hooks/useVisiblePolling'

// ─── FilterDropdown (matches EV page style) ───────────────────────────────────

const FilterDropdown = ({ isOpen, onToggle, buttonContent, children }) => (
  <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
    <style>{`
      /* Rows sit on glass, so they are lit rather than outlined: fills and a
         1px top highlight, no borders. Padding is IDENTICAL in every state —
         the old selected style swapped a 2px left border for 2px of padding,
         which nudged the label sideways as you moved down the list. */
      .odds-dd-item {
        padding: 5px 10px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 12.5px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
        color: #b8c5d3;
        font-weight: 400;
        transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
        white-space: nowrap;
      }
      .odds-dd-item:hover {
        background: rgba(255,255,255,0.055);
        color: #e2e8f0;
      }
      /* Selected: a fill that is brightest at the leading edge and falls away
         across the row. It reads as direction the way the old 2px bar did, but
         as light on a surface instead of a rule drawn beside it. The two inset
         shadows are the glass: a bright lip along the top edge, and a faint
         ring holding the shape together. */
      .odds-dd-item.selected {
        color: #f1f5f9;
        font-weight: 600;
        background: linear-gradient(90deg,
          rgba(59,130,246,0.30) 0%,
          rgba(59,130,246,0.11) 58%,
          rgba(59,130,246,0.03) 100%);
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.10),
          inset 0 0 0 1px rgba(96,165,250,0.22);
      }
      .odds-dd-item.selected:hover {
        background: linear-gradient(90deg,
          rgba(59,130,246,0.38) 0%,
          rgba(59,130,246,0.16) 58%,
          rgba(59,130,246,0.05) 100%);
      }
      /* Multi-select row. Same lit surface as .odds-dd-item: the highlight is
         the state. Select All / Clear All and the button's "(2/3)" counter
         carry the totals. */
      .odds-dd-check {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 5px 8px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 12.5px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
        color: #8397ad;
        font-weight: 400;
        transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
        white-space: nowrap;
      }
      .odds-dd-check:hover {
        background: rgba(255,255,255,0.055);
        color: #e2e8f0;
      }
      .odds-dd-check.checked {
        color: #f1f5f9;
        font-weight: 500;
        background: linear-gradient(90deg,
          rgba(59,130,246,0.30) 0%,
          rgba(59,130,246,0.11) 58%,
          rgba(59,130,246,0.03) 100%);
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.10),
          inset 0 0 0 1px rgba(96,165,250,0.22);
      }
      .odds-dd-scroll::-webkit-scrollbar {
        width: 2px;
      }
      .odds-dd-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .odds-dd-scroll::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.14);
        border-radius: 2px;
      }
      .odds-dd-scroll::-webkit-scrollbar-thumb:hover {
        background: rgba(255,255,255,0.24);
      }
      .odds-utility-btn {
        padding: 4px 10px;
        /* Clear glass, the same as the page's header buttons
           (.tool-unified .bc-header .bc-btn in app.css). */
        background: rgba(148, 163, 184, 0.03);
        color: #cbd5e1;
        border: 1px solid rgba(148, 163, 184, 0.16);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
        border-radius: 6px;
        cursor: pointer;
        font-size: 12px;
        font-weight: 500;
        font-family: 'Geist', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        transition: all 0.15s ease;
      }
      .odds-utility-btn:hover {
        background: rgba(148, 163, 184, 0.07);
        border-color: rgba(148, 163, 184, 0.3);
        color: #f1f5f9;
      }
      .odds-search-input {
        width: 100%;
        box-sizing: border-box;
        padding: 7px 11px;
        margin-bottom: 10px;
        border-radius: 6px;
        border: 1px solid rgba(255,255,255,0.10);
        background: rgba(255,255,255,0.04);
        color: #e2e8f0;
        font-size: 12.5px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
        outline: none;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
      }
      .odds-search-input::placeholder { color: #64748b; }
      .odds-search-input:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 2px rgba(59,130,246,0.18);
      }
    `}</style>
    <button
      onClick={onToggle}
      className={`bc-btn${isOpen ? ' active' : ''}`}
    >
      <span>{buttonContent}</span>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
        {isOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
      </svg>
    </button>
    {isOpen && (
      <div style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 1000,
        // Translucent + blurred: the panel picks up the page beneath it instead
        // of sitting on it as a flat slab. saturate lifts the colour the blur
        // washes out. The webkit prefix is still required by Safari.
        background: 'rgba(12,19,36,0.78)',
        backdropFilter: 'blur(22px) saturate(160%)',
        WebkitBackdropFilter: 'blur(22px) saturate(160%)',
        // Hairline in light, not a dark rule — a dark border on a translucent
        // panel reads as a gap. The inset highlight is the lit top edge.
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        padding: '12px',
        boxShadow: [
          '0 24px 56px rgba(0,0,0,0.60)',
          '0 2px 8px rgba(0,0,0,0.40)',
          'inset 0 1px 0 rgba(255,255,255,0.07)',
        ].join(', '),
        marginTop: '6px',
      }}>
        {children}
      </div>
    )}
  </div>
)

// ─── Helpers ──────────────────────────────────────────────────────────────────

// API returns one object per bookmaker per game — merge into one object per match.
//
// Two entries merge when they carry the same two teams AND start within this
// window of each other, which absorbs books disagreeing on the start time while
// keeping doubleheaders and genuinely separate fixtures apart.
//
// The window is PER SPORT because the sports disagree on how firm a start time
// is, and one number cannot serve both ends of that.
//
// MLB is the tight one, and it is tight because of DOUBLEHEADERS: two games,
// same two teams, same ballpark, a few hours apart. Those are separate fixtures
// and must stay separate rows, so the window has to close before the second one
// starts. Widening MLB merges a doubleheader into a single row carrying both
// games' prices.
//
// Everything else is loose. Tennis is why: it is scheduled "not before" (a match
// starts when the court frees up), so books publish start times hours apart for
// the same match and revise them all day. Too tight and one tennis match becomes
// one row per bookmaker. Other sports have no doubleheader to protect, so they
// take the loose window too rather than carrying a third value.
//
// The cost at the loose end, worth knowing: in tennis the key is two PLAYER
// names, and the same two players can meet twice at one event (a round-robin
// rematch at ATP Finals). Two such matches inside the window merge into one row
// and stack both sets of prices.
//
// NOT to be confused with the API's 10-minute staleness cutoff (`last_update`
// in public-api.py's fetch_odds_data), which drops books that have gone quiet
// before they ever reach the browser. Different mechanism, different purpose.

const GAME_GROUP_THRESHOLD_HOURS = 6
const GAME_GROUP_THRESHOLD_HOURS_BY_SPORT = { MLB: 1.8 }
const groupThresholdFor = (sportId) =>
  GAME_GROUP_THRESHOLD_HOURS_BY_SPORT[sportId] ?? GAME_GROUP_THRESHOLD_HOURS

const groupGamesByMatch = (games, sportId) => {
  const threshold = groupThresholdFor(sportId)
  const groups = [] // [{ baseKey, timeHours, game, bookmakers }]

  games.forEach(game => {
    // Build a normalized, order-independent key so the same fixture from different feeds
    // merges into one row. Trim because some feeds send team names with leading/trailing
    // whitespace (e.g. " Baltimore Orioles"), and sort so home/away order doesn't matter.
    const baseKey = [game.game.home_team, game.game.away_team]
      .map(t => (t || '').trim())
      .sort()
      .join('__')
    const timeStr = game.game.commence_time
    let timeHours = null

    if (timeStr) {
      try {
        const t = new Date(timeStr.endsWith('Z') ? timeStr : timeStr + 'Z')
        timeHours = t.getTime() / (1000 * 3600)
      } catch {}
    }

    const matched = groups.find(g =>
      g.baseKey === baseKey &&
      (timeHours === null || g.timeHours === null || Math.abs(g.timeHours - timeHours) <= threshold)
    )

    // The API returns ONE ENTRY PER BOOKMAKER, so `game.game_url` on this entry
    // is THIS bookmaker's link to the fixture, not a shared one — the same match
    // arrives three times with a colossalbet, a nextbet and a betdeluxe url.
    // Merging the entries therefore has to carry the url down onto the book, or
    // it is lost and every logo in the group points at whichever entry happened
    // to land first.
    const withUrl = game.bookmakers.map(bk => ({ ...bk, game_url: bk.game_url ?? game.game?.game_url ?? null }))

    if (matched) {
      withUrl.forEach(bk => matched.bookmakers.push(bk))
    } else {
      groups.push({ baseKey, timeHours, game: game.game, bookmakers: withUrl })
    }
  })

  return groups.map(g => ({ game: g.game, bookmakers: g.bookmakers }))
}

const formatTime = (timeStr) => {
  if (!timeStr) return '—'
  try {
    return new Date(timeStr + 'Z').toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  } catch { return timeStr }
}

// For a given outcome sortKey + bestPrice, find which bookmaker has it
// Consistent outcome key used across getOutcomeRows, getBestOdds, findAllBestBookies
// `description` qualifies an outcome whose `name` alone is ambiguous. The
// winning_margin_* markets price TWO BANDS PER TEAM, so the team name repeats
// and only the band ("Little Win 1-39" / "Big Win 40+") separates them —
// without it here the two bands collapse into one row and overwrite each
// other's prices. No other market sends the field, so nothing else changes.
// `description` is keyed ONLY on outcomes with no point. It exists for the
// winning_margin_* markets, which price two bands per team and are separated by
// nothing else — and those carry no point (their label is built from the
// description branch below, which only runs when point is null).
//
// It used to be keyed unconditionally, on the assumption that no other market
// sends the field. That is not true: at least one book attaches a description to
// Asian handicap outcomes, which split its price into a SECOND row identical to
// the first — same team, same line, same label, because the label never shows a
// description once a point exists. One row held that book alone and the other
// held every other book.
// team_name is keyed ONLY on an Over/Under outcome, which is the one place it
// carries information: a team total is "Over 4.5" and the team is the only thing
// telling two of them apart.
//
// Anywhere else the outcome is already NAMED after its team, so team_name is at
// best redundant — and at least one book sends it wrong. TAB prices Asian
// handicaps as:
//     { name: "Philadelphia Phillies", point: -1.5, team_name: "Atlanta Braves" }
// i.e. team_name holds the OPPONENT, and only on the negative side. Keying it
// gave TAB "Atlanta Braves_Philadelphia Phillies_-1.5" where every other book
// produced "Philadelphia Phillies_-1.5", so TAB's price split off into a second
// row for the same bet — one row holding TAB alone, the other holding the field.
const isTeamQualified = (outcome) => outcome.name === 'Over' || outcome.name === 'Under'

const outcomeKey = (outcome) => outcome.player_name
  ? `${outcome.player_name}_${outcome.name}_${outcome.point ?? ''}`
  : outcome.point != null
    ? `${outcome.team_name && isTeamQualified(outcome) ? outcome.team_name + '_' : ''}${outcome.name}_${outcome.point}`
    : `${outcome.team_name ? outcome.team_name + '_' : ''}${outcome.name}_${outcome.description ?? ''}`

// The subject a FLAT row is about, for its thumbnail.
//
// Flat rows are the markets with no line to group on - outrights (Brownlow
// Winner, Premiership Winner), tryscorers, goalscorers. A banded market puts
// its image on the player/team band instead; these have no band, so the only
// thing identifying the row is the outcome itself.
//
// *** THE NAME ALONE CANNOT BE TYPED. *** On these markets `name` is a bare
// string that may be a person ("Nick Daicos"), a club ("Brisbane Lions") or
// neither ("Any other player", "11-12 Votes"), and nothing in the payload says
// which. It is handed to SubjectImage as `subject_name`, which offers BOTH
// candidate urls and lets the one that exists win - see the note there. A name
// that is neither resolves to no image and falls through to the sport glyph,
// which is what it would have drawn anyway.
const flatRowSubject = (row, game) => {
  // Every book prices the same outcome, so the first source describes the row.
  const outcome = Object.values(row.sources || {})[0]?.outcome
  if (!outcome) return null

  // Where the feed DOES type the subject, trust it - no 404 needed.
  if (outcome.player_name) return { player_name: outcome.player_name }
  // team_name is not trusted on a lined market: at least one book sends the
  // OPPONENT there, the trap teamOf() documents in trackFromOdds.js.
  if (outcome.team_name && outcome.point == null) return { team_name: outcome.team_name }

  const name = outcome.name
  if (!name) return null
  // A fixture's own team is known outright.
  if (name === game.game.home_team || name === game.game.away_team) {
    return { team_name: name }
  }
  return { subject_name: name }
}

// Correct-score outcomes carry two dynamic "{player}" fields holding each
// player's set count (the API strips the " score" suffix). Standard outcome
// keys aren't players, so anything else with a numeric value is a player score.
const CS_STD_KEYS = new Set(['name', 'price', 'point', 'team_name', 'player_name',
  // Exchange depth (Kalshi, Polymarket). Both are numbers and neither is a
  // player score: an exchange outcome carries EXACTLY two of them, which is the
  // same shape a correct-score outcome has, so without this it was read as one
  // and labelled "size 1669.3-1218.47" on its own row instead of joining the
  // team's row. `alt_prices` is an array and was never caught by the filter.
  'size', 'liquidity'])
// Only correct-score markets have per-competitor score fields, so the market key
// gates this - a numeric field added to any other market can no longer turn a
// row into a scoreline.
const CORRECT_SCORE_KEYS = new Set(['correct_score', 'correct_score_1st_half',
  'correct_score_2nd_half', 'correct_score_sets'])
const getScoreFields = (outcome, marketKey) =>
  (marketKey != null && !CORRECT_SCORE_KEYS.has(marketKey))
    ? []
    : Object.entries(outcome).filter(([k, v]) => !CS_STD_KEYS.has(k) && typeof v === 'number')

// Exchange liquidity, for the cell subtitle: dollars resting at the displayed
// price. Bookmakers never send it, so this returns null for them and the cell
// renders exactly as before. Compact ($9.3k) to fit under a price.
const formatLiquidity = (value) => {
  if (typeof value !== 'number' || !isFinite(value) || value <= 0) return null
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}m`
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`
  return `$${Math.round(value)}`
}

// Build a "Winner scoreline" label for a correct-score outcome, e.g.
// "Alejandro Tabilo 3-0". Equal scores (a draw, e.g. soccer 1-1) read
// "Draw 1-1" — nobody wins. Falls back to the bare scoreline name if the score
// fields aren't present (e.g. malformed data).
const correctScoreLabel = (outcome) => {
  const scores = getScoreFields(outcome)
  if (scores.length !== 2) return outcome.name
  const [a, b] = scores
  if (a[1] === b[1]) return `Draw ${a[1]}-${b[1]}`
  const [winner, wScore, lScore] = a[1] > b[1] ? [a[0], a[1], b[1]] : [b[0], b[1], a[1]]
  return `${winner} ${wScore}-${lScore}`
}

// MMA outcome order. Method of Victory / Round Betting rows are "<Fighter> —
// <method or round>" and How Fight Ends rows are the bare method, so without
// this they came out in whichever order the first book listed them. Compared
// on the part after the fighter; only applies when BOTH rows are in the list,
// so no other market's order changes except Yes/No markets, which now read
// Yes first (they were alphabetical: No, Yes).
const MMA_OUTCOME_ORDER = ['KO/TKO', 'Submission', 'Round 1', 'Round 2', 'Round 3',
  'Round 4', 'Round 5', 'Decision', 'Draw', 'Yes', 'No']
const mmaOrder = (label) => MMA_OUTCOME_ORDER.indexOf(label.split(' — ').pop())

const findAllBestBookies = (game, sortKey, bestPrice, selectedBookmakers, marketKeys) => {
  const found = []
  for (const bk of game.bookmakers) {
    if (!selectedBookmakers.has(bk.name)) continue
    for (const market of bk.markets) {
      if (!marketKeys.includes(market.key)) continue
      for (const outcome of market.outcomes) {
        if (outcomeKey(outcome) === sortKey && adjustPrice(outcome.price, bk.name) === bestPrice) found.push(bk.name)
      }
    }
  }
  return [...new Set(found)]
}

// marketKeys is an array — find best price across selected bookmakers only
const getBestOdds = (games, marketKeys, selectedBookmakers) => {
  const best = {}
  games.forEach(game => {
    const gameId = `${game.game.home_team}_${game.game.away_team}_${game.game.commence_time}`
    game.bookmakers.forEach(bk => {
      if (!selectedBookmakers.has(bk.name)) return
      bk.markets.filter(m => marketKeys.includes(m.key)).forEach(market => {
        market.outcomes.forEach(outcome => {
          const key = `${gameId}_${outcomeKey(outcome)}`
          // Same adjustment as the cell, or the best-odds column would
          // disagree with the row it is summarising.
          const p = adjustPrice(outcome.price, bk.name)
          if (!best[key] || p > best[key]) best[key] = p
        })
      })
    })
  })
  return best
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  container: { width: '100%', margin: 0, padding: 0, backgroundColor: '#010714', minHeight: '100vh', boxSizing: 'border-box' },
  content:   { padding: 0 },
  gamesList:  { padding: '0 24px 24px' },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  filterBar: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  actionBtn: {
    border: '1px solid #35486b', borderRadius: '6px', padding: '6px 12px',
    background: 'transparent',
    color: '#f1f5f9', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
    display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s ease',
  },
  actionBtnActive: {
    border: '1px solid #22c55e', borderRadius: '6px', padding: '6px 12px',
    backgroundColor: 'rgba(34,197,94,0.1)', color: '#22c55e',
    cursor: 'pointer', fontSize: '13px', fontWeight: '500',
    display: 'flex', alignItems: 'center', gap: '6px',
  },
  resultCount: { fontSize: '13px', color: '#64748b' },
  lastUpdate:  { fontSize: '12px', color: '#475569', marginLeft: 'auto' },
  divider:     { width: '1px', height: '20px', backgroundColor: '#1b2538', margin: '0 4px' },
  bkFilter:    { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' },
  bkPill: {
    padding: '4px 10px', borderRadius: '12px',
    border: '1px solid #1b2538', backgroundColor: 'transparent',
    color: '#94a3b8', cursor: 'pointer', fontSize: '11px', fontWeight: '500',
  },
  bkPillActive: {
    padding: '4px 10px', borderRadius: '12px',
    border: '1px solid #7c3aed', backgroundColor: 'rgba(124,58,237,0.15)',
    color: '#a78bfa', cursor: 'pointer', fontSize: '11px', fontWeight: '600',
  },
  card: {
    backgroundColor: 'transparent',
    padding: '16px', marginBottom: 0,
  },
  stateBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '80px 24px',
    color: '#475569', textAlign: 'center', gap: '12px',
  },
  stateIcon:    { fontSize: '40px', opacity: 0.5 },
  stateText:    { fontSize: '15px', color: '#64748b' },
  stateSubText: { fontSize: '13px', color: '#475569' },
  oddsTable:     { width: '100%', borderCollapse: 'collapse' },
  oddsTableHead: { backgroundColor: '#0a1020' },
  gameHeaderTd: {
    padding: '8px 16px', backgroundColor: '#0a1020',
    borderTop: '2px solid #1e3a5f', borderBottom: '1px solid #1b2538',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  gameTitle: { fontSize: '13px', fontWeight: '600', color: '#e2e8f0', letterSpacing: '0.01em' },
  gameSep:   { fontSize: '12px', color: '#26354f' },
  gameTime:  { fontSize: '12px', color: '#64748b' },
  oddsThOutcome: {
    padding: '11px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700',
    color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid #1b2538', borderRight: '1px solid #1b2538', minWidth: '150px',
    position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#0a1020',
  },
  oddsThBookmaker: {
    padding: '11px 6px', textAlign: 'center',
    borderBottom: '1px solid #1b2538', borderRight: '1px solid #1b2538', minWidth: '60px',
    position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#0a1020',
  },
  oddsTd: {
    padding: '11px 6px', textAlign: 'center', fontSize: '13px', fontWeight: '700',
    color: '#b0bec9', borderBottom: '1px solid #1b2538', borderRight: '1px solid #1b2538',
  },
  oddsTdBest: {
    padding: '11px 6px', textAlign: 'center', fontSize: '13px', fontWeight: '700',
    color: '#22c55e', borderBottom: '1px solid #1b2538', borderRight: '1px solid #1b2538',
    backgroundColor: 'rgba(34,197,94,0.06)',
  },
  oddsTdOutcome: {
    padding: '11px 12px', textAlign: 'left', fontSize: '13px',
    color: '#cbd5e1', fontWeight: '500', borderBottom: '1px solid #1b2538', borderRight: '1px solid #1b2538',
  },
  oddsTdMissing: {
    padding: '11px 6px', textAlign: 'center', fontSize: '13px',
    color: '#33455f', borderBottom: '1px solid #1b2538', borderRight: '1px solid #1b2538',
  },
  // The dropdown is two columns of DIFFERENT things — sport categories on the
  // left, market families on the right — and they used to share one label style,
  // so "Basketball" and "Player Props" were indistinguishable. Three ranks now,
  // and rank tracks depth: a heading is never quieter than what sits under it.
  //
  // Rank 1, column header ("Sport" / "Market"). Smallest and widest-tracked;
  // it labels the whole column, so it reads as a caption rather than an item.
  dropdownColumnLabel: {
    fontSize: '9.5px', fontWeight: '700', color: '#7f8ea6',
    textTransform: 'uppercase', letterSpacing: '0.12em',
    padding: '6px 10px 7px',
  },
}

// ─── CheckboxItem (matches EV page) ───────────────────────────────────────────

const CheckboxItem = ({ item, selected, onToggle, logo }) => (
  <div onClick={() => onToggle(item)} className={`odds-dd-check${selected ? ' checked' : ''}`}>
    {logo && <img src={logo} alt={item} style={{ width: '18px', height: '18px', borderRadius: '3px', flexShrink: 0 }} />}
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item}</span>
  </div>
)

// ─── Component ────────────────────────────────────────────────────────────────

// Shortened category labels for the narrow Sport column. Only categories too
// wide for it appear here — everything else renders its SPORT_HIERARCHY key
// unchanged, so this map stays as small as the column demands.
const CATEGORY_SHORT_NAMES = {
  'American Football': 'Am. Football',
}

const DEFAULT_SPORT  = 'NBA'
const DEFAULT_MARKET = 'Head To Head'

// Flat one-player-per-row outright ladders: one row per player, one price, no
// Over/Under pairing and no line to sort on. They DEFAULT to shortest price
// first — the way every book presents an outright, favourite at the top — and
// the Best column cycles shortest → longest → A-Z from there.
//
// AFL futures get the same order without being listed here: a futures row has
// no point, team or player name to sort on, so getOutcomeRows sorts it on
// game.futuresTitle before reaching any of these branches.
const ODDS_SORTABLE_MARKETS = new Set([
  'First Tryscorer',
  'First or Second Tryscorer',
  'First, Second or Third Tryscorer',
  'First Touchdown Scorer',
])

export default function ODDS1({ isVisible, isPreview }) {
  const { oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('odds')

  const tableWrapperRef = useRef(null)
  const [tableHeight, setTableHeight] = useState(400)

  const [selectedSport, setSelectedSport] = useState(DEFAULT_SPORT)
  // Sport and League are two columns, not one flat list: together they were 41
  // rows (33 leagues under 8 headings) in a 160px column, and tennis alone is 15
  // of them. Seeded from the selected sport so opening the dropdown lands on the
  // league you are already on rather than a list to hunt through.
  // Which toolbar dropdown is open, by name — at most one ever is. This was
  // five separate booleans, and every toggle had to remember to clear the other
  // four, so a newly added dropdown stayed open behind whichever one you opened
  // next. One value cannot get out of step with itself.
  const [openDropdown, setOpenDropdown] = useState(null)
  // Brownlow 3 Votes: which round is showing. null = not chosen yet, which
  // resolves to the earliest round that has prices (see effectiveRound).
  const [selectedRound, setSelectedRound] = useState(null)
  const toggleDropdown = (name) => setOpenDropdown(prev => (prev === name ? null : name))
  const closeDropdowns = () => setOpenDropdown(null)

  const [activeCategory, setActiveCategory] = useState(LEAGUE_TO_GROUP[DEFAULT_SPORT] || null)

  // Per virtual league, the member ids that last came back with games. Lets
  // auto-refresh ask only those instead of repeating the full fan-out every
  // 30 seconds. Null/empty means "ask everything again".
  const liveMembersRef = useRef({})

  const [hiddenTournaments, setHiddenTournaments] = useState(new Set())

  // Browsing state for the market columns. Null means "follow the current
  // selection"; set while the user explores a different group/market without
  // having picked a period yet, since only that final click refetches.
  const [marketGroupOverride, setMarketGroupOverride] = useState(null)
  const [baseKeyOverride, setBaseKeyOverride] = useState(null)

  const toggleTournament = (name) => setHiddenTournaments(prev => {
    const next = new Set(prev)
    next.has(name) ? next.delete(name) : next.add(name)
    return next
  })
  const [selectedMarketLabel, setSelectedMarketLabel] = useState(DEFAULT_MARKET)
  // Best-odds column sort: null = default (market/alpha) order, 'asc' / 'desc' = by best price.
  // Applies per-game to flat markets (tryscorers, H2H, etc.). Clicking the "Best" header cycles it.
  const [bestSort, setBestSort] = useState(null)
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [selectedBookmakers, setSelectedBookmakers] = useState(new Set(ALL_BOOKMAKERS))

  // Selections waiting in the betslip. A price is added by clicking it and
  // removed by clicking it again, so the table itself shows what is in the slip
  // and no dialog interrupts the comparing this page exists for.
  const [slipLegs, setSlipLegs] = useState([])
  const [slipToast, setSlipToast] = useState(null)

  const slipKeys = useMemo(() => new Set(slipLegs.map(slipKey)), [slipLegs])

  const removeFromSlip = useCallback((key) => {
    setSlipLegs(prev => prev.filter(leg => slipKey(leg) !== key))
  }, [])

  // The table's price is a snapshot; the one actually taken is often a tick
  // different by the time the bet is placed, so the slip lets it be corrected.
  // Held as the raw string while typing so a half-entered "1." is not coerced
  // to a number and the caret thrown to the end.
  const setLegOdds = useCallback((key, value) => {
    setSlipLegs(prev => prev.map(leg => (slipKey(leg) === key ? { ...leg, odds: value } : leg)))
  }, [])

  /** One cell as an opportunity. The single place a cell becomes a bet. */
  const oppForCell = useCallback((game, source, bookmaker, price) => oppFromOutcome({
    outcome:   source.outcome,
    marketKey: source.marketKey,
    game,
    bookmaker,
    price,
    sport:     selectedSport,
    // Looked up off the BOOK rather than the game: the merge above put each
    // entry's url on its own bookmaker, so this is the page for the book whose
    // price was actually clicked.
    gameUrl:   game?.bookmakers?.find(b => b.name === bookmaker)?.game_url ?? null,
  }), [selectedSport])

  /** That cell's slip key, or null when there is nothing trackable in it. */
  const cellSlipKey = useCallback((game, source, bookmaker, price) => {
    const opp = oppForCell(game, source, bookmaker, price)
    return opp ? slipKey(opp) : null
  }, [oppForCell])

  /** Click a price to put it in the slip, click it again to take it out. */
  const toggleOutcome = (game, row, bookmaker, price) => {
    const source = row?.sources?.[bookmaker]
    if (isPreview || !source) return
    const opp = oppForCell(game, source, bookmaker, price ?? row.prices?.[bookmaker])
    if (!opp) return

    const key = slipKey(opp)
    setSlipLegs(prev => prev.some(leg => slipKey(leg) === key)
      ? prev.filter(leg => slipKey(leg) !== key)
      : [...prev, opp])
  }

  // Deliberately NOT cleared when the sport changes. Building a multi across
  // codes is a normal thing to want, and the slip lists every leg with its own
  // remove button, so a selection whose table is no longer on screen is still
  // reviewable. Legs from another sport simply stop showing as selected cells.
  const [expandedPlayers, setExpandedPlayers] = useState(new Set())
  // Empty Set = show all games (default). Resets when a new market is picked.
  const [hiddenGames, setHiddenGames] = useState(new Set())
  // Empty Set = show all players (default). Resets when a new market is picked.
  const [hiddenPlayers, setHiddenPlayers] = useState(new Set())
  const [playerSearch, setPlayerSearch] = useState('')
  const [gameSearch, setGameSearch] = useState('')

  // Resolve selected market label → API keys
  const selectedMarketKeys = selectedMarketLabel
    ? marketKeysFor(selectedMarketLabel)
    : []

  // Only the tryscorer ladder markets get the clickable best-odds sort.
  const oddsSortable = ODDS_SORTABLE_MARKETS.has(selectedMarketLabel)

  // Fetch on mount with defaults
  useEffect(() => {
    if (!isVisible) return
    const keys = marketKeysFor(DEFAULT_MARKET)
    fetchOdds(DEFAULT_SPORT, keys)
    // fetchOdds is a stable useCallback; intent is to fetch once when the page becomes visible
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  // Auto-refresh
  useVisiblePolling(
    () => fetchOdds(selectedSport, selectedMarketKeys),
    30000,
    autoRefresh && !!selectedSport && selectedMarketKeys.length > 0 && isVisible
  )

  // One real sport id. Every game comes back tagged with the tournament it
  // belongs to: multi-tournament sports (Challenger) already carry one, and for
  // a single-tournament id the sport IS the tournament, so it is filled in from
  // the display name. Downstream nothing has to know which kind it came from.
  const fetchOneSport = async (sportId, marketKeys) => {
    // Futures has no fixture, so it has its own endpoint and its own proxy. The
    // API is asked for the REAL sport (AFL) — AFL_FUTURES is a dropdown id that
    // exists only in this app — and the market-major response is un-pivoted
    // into the games[] shape the table already renders.
    if (isFuturesLeague(sportId)) {
      const { data, error: fnError } = await supabase.functions.invoke('futures-proxy', {
        body: { sport_id: futuresSportId(sportId), market_type: marketKeys }
      })
      if (fnError) throw fnError
      // The REAL sport id ("AFL"), not the AFL_FUTURES dropdown id - it is what
      // the image lookup resolves its folder from.
      return futuresToGames(data, futuresSportId(sportId))
    }

    const { data, error: fnError } = await supabase.functions.invoke('odds-proxy', {
      body: { sport_id: sportId, market_type: marketKeys }
    })
    if (fnError) throw fnError
    return (data?.games || []).map(g => ({
      ...g,
      // sport is the MEMBER league (EPL), never the virtual id (Soccer) that
      // fanned out to it — team crests live in per-league folders.
      game: { ...g.game, sport: sportId, tournament: g.game?.tournament || SPORT_DISPLAY_NAMES[sportId] || sportId },
    }))
  }

  const fetchOdds = useCallback(async (sportId, marketKeys) => {
    try {
      setLoading(true)
      setError(null)

      if (isVirtualLeague(sportId)) {
        // Fan out across the member tournaments. Out-of-season ones return an
        // empty list and are NOT charged (public-api.py only deducts credits
        // when games come back), so the cost of asking all of them is the round
        // trips, not credits.
        //
        // Auto-refresh then narrows to whichever members actually had games, so
        // the wide fan-out happens on selection and manual refresh only. A
        // tournament that starts mid-session is picked up the next time the
        // league is selected or refreshed.
        const all = VIRTUAL_LEAGUES[sportId].members
        const live = liveMembersRef.current[sportId]
        const targets = (live && live.length > 0) ? live : all

        const results = await Promise.allSettled(
          targets.map(id => fetchOneSport(id, marketKeys))
        )
        const merged = []
        const nowLive = []
        results.forEach((r, i) => {
          if (r.status !== 'fulfilled') return
          if (r.value.length > 0) {
            nowLive.push(targets[i])
            merged.push(...r.value)
          }
        })
        // Every member failing is a real error; some failing is not, since most
        // are expected to be empty anyway.
        if (merged.length === 0 && results.every(r => r.status === 'rejected')) {
          throw new Error(results[0]?.reason?.message || 'Failed to fetch odds')
        }
        // Re-widen when the narrowed set has gone quiet, so a finished
        // tournament cannot pin the page to an empty view.
        liveMembersRef.current[sportId] = nowLive.length > 0 ? nowLive : null
        setGames(merged)
      } else {
        setGames(await fetchOneSport(sportId, marketKeys))
      }

      setLastUpdate(new Date())
    } catch (err) {
      setError(err.message || 'Failed to fetch odds')
      setGames([])
    } finally {
      setLoading(false)
    }
    // fetchOneSport closes over nothing that changes between renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const handleSportSelect = (sportId) => {
    setSelectedSport(sportId)
    // Tournament names are per league; carrying a selection across would hide
    // rows for a reason the user cannot see.
    setHiddenTournaments(new Set())
    setSelectedMarketLabel(null)
    setGames([])
    setError(null)
    // keep dropdown open so user can pick market next
  }

  const handleMarketSelect = (label) => {
    setMarketGroupOverride(null)
    setBaseKeyOverride(null)
    setSelectedMarketLabel(label)
    const keys = marketKeysFor(label)
    setHiddenGames(new Set()) // new market → show all games
    setHiddenPlayers(new Set()); setPlayerSearch('')
    setGameSearch('')
    fetchOdds(selectedSport, keys)
    closeDropdowns()
  }

  // Grouped data
  // Rounds present in the loaded data - only markets split per round (Brownlow
  // 3 Votes) carry futuresRound, so for everything else this is empty and the
  // Round filter never appears.
  const availableRounds = useMemo(() => (
    [...new Set(games.map(g => g.game.futuresRound).filter(r => r != null))].sort((a, b) => a - b)
  ), [games])
  // The chosen round if it still has data, else the earliest one that does, so
  // the page never opens on an empty round.
  const effectiveRound = availableRounds.includes(selectedRound) ? selectedRound : (availableRounds[0] ?? null)

  const groupedGames = useMemo(() => {
    // Filter to ONE round BEFORE grouping. These blocks have no start time, so
    // groupGamesByMatch merges on the two team names alone - a pair that meets
    // twice in a season would collapse into one block if both rounds went in.
    const roundGames = effectiveRound == null
      ? games
      : games.filter(g => g.game.futuresRound == null || g.game.futuresRound === effectiveRound)
    const grouped = groupGamesByMatch(roundGames, selectedSport)
    return grouped.sort((a, b) => {
      const tA = a.game.commence_time ? new Date(a.game.commence_time.endsWith('Z') ? a.game.commence_time : a.game.commence_time + 'Z').getTime() : 0
      const tB = b.game.commence_time ? new Date(b.game.commence_time.endsWith('Z') ? b.game.commence_time : b.game.commence_time + 'Z').getTime() : 0
      return tA - tB
    })
  }, [games, selectedSport, effectiveRound])

  useEffect(() => {
    const calcHeight = () => {
      if (tableWrapperRef.current) {
        const top = tableWrapperRef.current.getBoundingClientRect().top
        setTableHeight(window.innerHeight - top - 8)
      }
    }
    const timer = setTimeout(calcHeight, 50)
    window.addEventListener('resize', calcHeight)
    return () => { clearTimeout(timer); window.removeEventListener('resize', calcHeight) }
  }, [isVisible, groupedGames.length])

  const allBookmakers = useMemo(() => {
    const bkSet = new Set()
    groupedGames.forEach(g => g.bookmakers.forEach(bk => bkSet.add(bk.name)))
    return Array.from(bkSet).sort()
  }, [groupedGames])

  const visibleBookmakers = allBookmakers.filter(bk => selectedBookmakers.has(bk))

  const toggleBookmakerSelection = (bk) => {
    setSelectedBookmakers(prev => {
      const next = new Set(prev)
      next.has(bk) ? next.delete(bk) : next.add(bk)
      return next
    })
  }

  const toggleCountryBookmakers = (country) => {
    const countryBks = BOOKMAKERS[country]
    const allSelected = countryBks.every(bk => selectedBookmakers.has(bk))
    setSelectedBookmakers(prev => {
      const next = new Set(prev)
      countryBks.forEach(bk => allSelected ? next.delete(bk) : next.add(bk))
      return next
    })
  }

  const gameKey = (g) => `${g.game.home_team}_${g.game.away_team}_${g.game.commence_time}`
  const gameLabel = (g) => g.game.futuresTitle || `${g.game.away_team} vs ${g.game.home_team}`

  // Distinct tournaments in what is currently loaded. Only ever more than one
  // for a multi-tournament sport (Challenger) or a fanned-out virtual league
  // (ATP) — every other sport yields exactly one and the filter stays hidden.
  const allTournaments = useMemo(() => {
    const set = new Set()
    groupedGames.forEach(g => { if (g.game?.tournament) set.add(g.game.tournament) })
    return [...set].sort()
  }, [groupedGames])

  const visibleGames = groupedGames.filter(g =>
    !hiddenGames.has(gameKey(g)) && !hiddenTournaments.has(g.game?.tournament)
  )

  const toggleGame = (key) => setHiddenGames(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  // Players present in the loaded market (player-prop markets only). Empty when not a prop market.
  const allPlayers = useMemo(() => {
    const set = new Set()
    groupedGames.forEach(g => g.bookmakers.forEach(bk =>
      bk.markets.filter(m => selectedMarketKeys.includes(m.key)).forEach(m =>
        m.outcomes.forEach(o => { if (o.player_name) set.add(o.player_name) })
      )
    ))
    return Array.from(set).sort()
    // selectedMarketKeys derives from selectedMarketLabel, which is keyed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedGames, selectedMarketLabel])

  // Set form of the above, so a player band can be told apart from a team-total
  // or game-total band in O(1). The band's label is just a parsed prefix — it is
  // a player name, a team name or a market name, and only this can say which.
  const playerNameSet = useMemo(() => new Set(allPlayers), [allPlayers])


  const togglePlayerFilter = (name) => setHiddenPlayers(prev => {
    const next = new Set(prev)
    next.has(name) ? next.delete(name) : next.add(name)
    return next
  })

  const bestOddsMap = useMemo(() => {
    if (!selectedMarketKeys.length || !groupedGames.length) return {}
    return getBestOdds(groupedGames, selectedMarketKeys, selectedBookmakers)
    // selectedMarketKeys derives from selectedMarketLabel, which is keyed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedGames, selectedMarketLabel, selectedBookmakers])

  const getOutcomeRows = (game) => {
    const outcomeMap = {}
    game.bookmakers.forEach(bk => {
      if (!selectedBookmakers.has(bk.name)) return
      bk.markets.filter(m => selectedMarketKeys.includes(m.key)).forEach(market => {
        market.outcomes.forEach(outcome => {
          if (outcome.player_name && hiddenPlayers.has(outcome.player_name)) return
          const isCorrectScore = getScoreFields(outcome, market.key).length === 2
          const label = isCorrectScore
            ? correctScoreLabel(outcome)
            // Yes/No at a round number. The generic point branch below would
            // read "Yes +2", so the round is named instead.
            : market.key === 'fight_to_start_round'
            ? `Start Round ${outcome.point} — ${outcome.name}`
            : outcome.player_name
            ? `${outcome.player_name} — ${outcome.name}${outcome.point != null ? ` ${Math.abs(outcome.point)}` : ''}`
            : outcome.point != null
              ? `${outcome.team_name && isTeamQualified(outcome) ? outcome.team_name + ' — ' : ''}${outcome.name} ${outcome.point > 0 && outcome.name !== 'Over' && outcome.name !== 'Under' ? '+' : ''}${outcome.point}`
              : outcome.description
                ? `${outcome.name} — ${outcome.description}`
                : outcome.name
          const sortKey = outcomeKey(outcome)
          if (!outcomeMap[sortKey]) {
            outcomeMap[sortKey] = { label, prices: {}, sources: {}, sortKey }
            // Tag correct-score rows with the winner + set counts for ordering.
            // Equal scores = a draw (grouped under 'Draw', sorted last).
            if (isCorrectScore) {
              const scores = getScoreFields(outcome)
              const [a, b] = scores
              const draw = a[1] === b[1]
              const [winner, wScore, lScore] = draw
                ? ['Draw', a[1], b[1]]
                : a[1] > b[1] ? [a[0], a[1], b[1]] : [b[0], b[1], a[1]]
              outcomeMap[sortKey].cs = { winner, wScore, lScore, draw }
            }
          }
          // Price AND market key, per BOOKMAKER.
          //
          // A row is one proposition, but the market behind it differs by book:
          // Bet365 may price "Over 0.5 hits" as batter_hits while BetRight lists
          // the same bet as a 1+ milestone. They collide on one row because some
          // feeds name milestone outcomes "Over" — correctly, since the two are
          // the same wager — but each book's own key is what its cell should
          // report.
          //
          // Recorded in step with the price, so the key always belongs to the
          // number on screen. Settlement is indifferent (both strip to the same
          // base_market_type, point and side); this is about the bet naming the
          // market the user will actually find at that book.
          // Exchange prices are fee-adjusted HERE, at the single point where a
          // price enters the row, so every consumer downstream - best-odds,
          // highlighting, sorting, the bet slip - compares like with like
          // without knowing anything about exchanges. Market-type agnostic:
          // the fee is a property of the contract, so it applies to h2h,
          // spreads, totals and player props alike. See config/exchanges.js.
          const cellPrice = adjustPrice(outcome.price, bk.name)
          const existing = outcomeMap[sortKey].prices[bk.name]
          if (!existing || cellPrice > existing) {
            outcomeMap[sortKey].prices[bk.name] = cellPrice
            outcomeMap[sortKey].sources[bk.name] = { outcome, marketKey: market.key }
          }
        })
      })
    })
    const rows = Object.values(outcomeMap)

    // Futures: SHORTEST PRICE FIRST, the way every book presents an outright —
    // favourite at the top, roughies at the bottom.
    //
    // Returned before the branches below because a futures row has no point, no
    // team and no player name for any of them to sort on, so it would fall
    // through to A-Z and scatter the 501.00 no-hopers among the favourites.
    //
    // Ordered on the BEST price across books, which is the number the page
    // highlights, so the order matches the column a reader actually scans.
    // Equal prices fall back to A-Z rather than whatever order the books came
    // back in, so the list does not reshuffle between refreshes.
    if (game.game.futuresTitle) {
      const best = (row) => {
        const prices = Object.values(row.prices)
        return prices.length ? Math.max(...prices) : Infinity
      }
      return rows.sort((a, b) => best(a) - best(b) || a.label.localeCompare(b.label))
    }

    const getPoint = (row) => {
      const m = row.sortKey.match(/_(-?\d+\.?\d*)$/)
      return m ? parseFloat(m[1]) : null
    }
    const getTeam = (row) => row.sortKey.replace(/_-?\d+\.?\d*$/, '')

    // Spread market: has negative point values (handicap lines)
    const isSpread = rows.some(r => (getPoint(r) ?? 0) < 0)

    if (isSpread) {
      const negRows = rows.filter(r => (getPoint(r) ?? 0) < 0)
        .sort((a, b) => Math.abs(getPoint(a)) - Math.abs(getPoint(b)) || a.label.localeCompare(b.label))
      const posRows = rows.filter(r => (getPoint(r) ?? 0) > 0)
      const noPointRows = rows.filter(r => getPoint(r) === null)
      const usedKeys = new Set()
      const paired = []

      negRows.forEach(neg => {
        paired.push(neg)
        const mirror = posRows.find(p =>
          !usedKeys.has(p.sortKey) &&
          Math.abs(getPoint(p)) === Math.abs(getPoint(neg)) &&
          getTeam(p) !== getTeam(neg)
        )
        if (mirror) { usedKeys.add(mirror.sortKey); paired.push(mirror) }
      })
      posRows.filter(p => !usedKeys.has(p.sortKey)).forEach(p => paired.push(p))
      return [...noPointRows, ...paired]
    }

    // Correct score (scoreline): group rows by winner (home first, away next,
    // draws last), then by winning score, then losing score.
    if (rows.length && rows.every(r => r.cs)) {
      const homeTeam = game.game.home_team
      const rank = (cs) => cs.draw ? 2 : cs.winner === homeTeam ? 0 : 1
      rows.sort((a, b) => {
        const ar = rank(a.cs), br = rank(b.cs)
        if (ar !== br) return ar - br
        if (a.cs.winner !== b.cs.winner) return a.cs.winner.localeCompare(b.cs.winner)
        if (a.cs.wScore !== b.cs.wScore) return a.cs.wScore - b.cs.wScore
        return a.cs.lScore - b.cs.lScore
      })
      return rows
    }

    // Totals or player props — group by team name, then point asc, Over before Under / player A-Z
    rows.sort((a, b) => {
      const aPoint = getPoint(a)
      const bPoint = getPoint(b)

      // Extract team prefix from label (e.g. "Portland Trail Blazers — Over 106.5" → "Portland Trail Blazers")
      // A bare "Draw" beside fighter-prefixed rows (Method of Victory, Round
      // Betting) would otherwise sort FIRST on its empty prefix.
      const prefixed = a.label.includes(' — ') || b.label.includes(' — ')
      const aTeam = a.label.includes(' — ') ? a.label.split(' — ')[0] : (prefixed && a.label === 'Draw' ? '\uffff' : '')
      const bTeam = b.label.includes(' — ') ? b.label.split(' — ')[0] : (prefixed && b.label === 'Draw' ? '\uffff' : '')
      if (aTeam !== bTeam) return aTeam.localeCompare(bTeam)
      const aMma = mmaOrder(a.label)
      const bMma = mmaOrder(b.label)
      if (aMma !== -1 && bMma !== -1 && aMma !== bMma) return aMma - bMma

      if (aPoint !== null && bPoint !== null) {
        if (aPoint !== bPoint) return aPoint - bPoint
        const aOver = a.label.includes('Over')
        const bOver = b.label.includes('Over')
        if (aOver && !bOver) return -1
        if (!aOver && bOver) return 1
        return 0
      }

      // No-point markets (H2H etc) — Home first, Away second, Draw last
      const homeTeam = game.game.home_team
      const awayTeam = game.game.away_team
      const rank = (label) => {
        if (label === homeTeam) return 0
        if (label === awayTeam) return 1
        if (label === 'Draw') return 2
        return 3
      }
      const aRank = rank(a.label)
      const bRank = rank(b.label)
      if (aRank !== bRank) return aRank - bRank

      // Player props — A-Z by player name
      const aPlayer = a.sortKey.split('_')[0]
      const bPlayer = b.sortKey.split('_')[0]
      if (aPlayer !== bPlayer) return aPlayer.localeCompare(bPlayer)
      const aOver = a.label.includes('Over')
      const bOver = b.label.includes('Over')
      if (aOver && !bOver) return -1
      if (!aOver && bOver) return 1
      return 0
    })

    return rows
  }

  // Group Over/Under rows into collapsible blocks by line. Handles player props (grouped by
  // player) and totals (grouped by team, or a single "Total Points" block for game totals).
  // Each group: { label, lines: [{ key, point, rows: [...] }], mainKey }
  // Lines are identified by `key`, not by `point`: a handicap market holds TWO
  // propositions at the same abs point (Braves -1.5/Phillies +1.5 and
  // Phillies -1.5/Braves +1.5), so a point alone cannot name one of them.
  // Main line = the line priced by the most bookmakers (tie → prices nearest evens).
  // Returns null unless every row is an Over/Under line and at least one group has alt lines.
  const getLineGroups = (rows) => {
    // sortKey shapes this has to read, straight out of outcomeKey():
    //   player prop  "Marcus Bontempelli_Over_20.5"      player_Over_point
    //   team total   "Collingwood_Over__45.5"            team_Over_<desc>_point
    //   game total   "Over__45.5"                        Over_<desc>_point
    //
    // The non-player branch of outcomeKey always emits a description segment,
    // and for a total that segment is EMPTY — hence the double underscore. The
    // old pattern required a digit immediately after "Over_", so every totals
    // market failed to parse and fell through to the flat, always-expanded
    // path; only player props ever grouped. (Which also left isTotals below
    // unreachable.) The description segment is optional here so all three
    // shapes parse.
    const parse = (r) => {
      const m = r.sortKey.match(/^(?:(.*?)_)?(Over|Under)_(?:([^_]*)_)?(-?\d+\.?\d*)$/)
      if (!m) return null
      return { group: m[1] || '', side: m[2], point: parseFloat(m[4]) }
    }
    if (!rows.length || rows.some(r => !parse(r))) return null

    const isTotals = rows.every(r => parse(r).group === '')

    const groups = new Map()
    // Game-level totals have no team/player prefix, so use the selected market's
    // own display name for the band (e.g. "Total Sets", "Total Games") instead of
    // a hardcoded "Total Points" — which was wrong for every non-points total.
    const totalsLabel = selectedMarketLabel || 'Total'
    rows.forEach(r => {
      const { group, point } = parse(r)
      const key = isTotals ? totalsLabel : group
      if (!groups.has(key)) groups.set(key, new Map())
      const lines = groups.get(key)
      const pk = String(point)
      if (!lines.has(pk)) lines.set(pk, { key: pk, point, rows: [] })
      lines.get(pk).rows.push(r)
    })

    const result = Array.from(groups.entries()).map(([label, lineMap]) => {
      const lines = Array.from(lineMap.values()).sort((a, b) => a.point - b.point)
      lines.forEach(l => l.rows.sort((a, b) => (b.label.includes('Over') ? 1 : 0) - (a.label.includes('Over') ? 1 : 0)))
      const bookCount = (line) => {
        const books = new Set()
        line.rows.forEach(r => Object.keys(r.prices).forEach(b => books.add(b)))
        return books.size
      }
      // Tiebreak on the ODDS, not on where the line sits. Two lines carried by
      // the same number of books are equally offered, and what separates them is
      // price: a main line is the one priced near even money, because a side
      // line is precisely the one that has to carry a lopsided price to be worth
      // listing. The old tiebreak took the line closest to the median POINT,
      // which is a proxy for the same idea and a worse one - it picks the middle
      // of the ladder even when the middle is priced 1.40/2.90.
      //
      // Judged on the BEST price per side, i.e. the number actually shown in the
      // row, and averaged across Over and Under so a line cannot read as even
      // off one side while the other sits at 1.40.
      const bestOf = (row) => {
        const prices = Object.values(row.prices).filter(Number.isFinite)
        return prices.length ? Math.max(...prices) : null
      }
      const evenness = (line) => {
        const prices = line.rows.map(bestOf).filter(p => p !== null)
        if (!prices.length) return Infinity
        return prices.reduce((sum, p) => sum + Math.abs(p - 2), 0) / prices.length
      }
      const main = lines.reduce((best, l) => {
        if (!best) return l
        const d = bookCount(l) - bookCount(best)
        if (d !== 0) return d > 0 ? l : best
        return evenness(l) < evenness(best) ? l : best
      }, null)
      return { label, lines, mainKey: main ? main.key : null }
    }).sort((a, b) => a.label.localeCompare(b.label))

    // Worth grouping if there are alt lines to hide OR more than one group to
    // separate. Team totals are the second case: each team may be priced at a
    // single line, so there is nothing to collapse, but banding them by team is
    // still what turns a flat list of every side's Over and Under into one block
    // per team. A single group with a single line (a game total) has neither, so
    // it stays flat exactly as before.
    if (result.length <= 1 && !result.some(g => g.lines.length > 1)) return null
    return result
  }

  // Group spread/handicap rows into one collapsible "Handicap" block, one entry
  // per PROPOSITION (a favourite side + its opposite-team mirror). sortKey format:
  // `Team_point`. Books can price BOTH directions at the same abs value (e.g.
  // Player -1.5 / Opp +1.5 AND Player +1.5 / Opp -1.5), so we key each line by the
  // negative side's signed `Team_point` — keeping both propositions rather than
  // collapsing to one. A pos row with no negative mirror becomes its own line.
  const getSpreadGroup = (rows) => {
    const getPoint = (r) => { const m = r.sortKey.match(/_(-?\d+\.?\d*)$/); return m ? parseFloat(m[1]) : null }
    const getTeam = (r) => r.sortKey.replace(/_-?\d+\.?\d*$/, '')
    if (!rows.length || rows.some(r => getPoint(r) === null)) return null

    const negRows = rows.filter(r => getPoint(r) < 0)
    const posRows = rows.filter(r => getPoint(r) > 0)
    // Only a real spread/handicap market has negative (favourite) lines. Without
    // them (e.g. Over/Under props like Player Sets Won at 0.5), this isn't a
    // spread — bail so the flat Over/Under path handles it.
    if (!negRows.length) return null
    const usedPos = new Set()
    const lines = []

    // Each negative favourite anchors a proposition; pair with its opposite-team
    // positive mirror at the same abs (each mirror used once).
    negRows.forEach(neg => {
      const mirror = posRows.find(p =>
        !usedPos.has(p.sortKey) &&
        Math.abs(getPoint(p)) === Math.abs(getPoint(neg)) &&
        getTeam(p) !== getTeam(neg)
      )
      if (mirror) usedPos.add(mirror.sortKey)
      lines.push({ key: neg.sortKey, point: Math.abs(getPoint(neg)), rows: mirror ? [neg, mirror] : [neg] })
    })
    // Positive rows with no negative mirror (rare) become standalone lines.
    posRows.filter(p => !usedPos.has(p.sortKey)).forEach(p => {
      lines.push({ key: p.sortKey, point: Math.abs(getPoint(p)), rows: [p] })
    })

    lines.sort((a, b) => a.point - b.point || (a.rows[0].label.localeCompare(b.rows[0].label)))
    if (lines.length < 2) return null

    const bookCount = (line) => {
      const set = new Set()
      line.rows.forEach(r => Object.keys(r.prices).forEach(b => set.add(b)))
      return set.size
    }
    const main = lines.reduce((best, l) => {
      if (!best) return l
      const d = bookCount(l) - bookCount(best)
      if (d !== 0) return d > 0 ? l : best
      return l.point < best.point ? l : best // tie → tightest handicap
    }, null)
    // mainKey, NOT mainPoint. Both propositions at 1.5 share point 1.5, so the
    // renderer's lookup would resolve to whichever sorted first (alphabetically
    // by team) rather than the one chosen here — which is how the Braves -1.5
    // side with 7 books missing was displayed while the fully priced
    // Phillies -1.5 side sat hidden behind "N lines".
    return [{ label: 'Handicap', lines, mainKey: main ? main.key : null }]
  }

  // The dropdown's three market columns. Built from the labels this sport
  // actually carries, so a sport never offers a period it cannot price.
  const marketTree = useMemo(
    () => buildMarketTree(SPORT_AVAILABLE_MARKETS[selectedSport] || []),
    [selectedSport]
  )
  // Where the CURRENT selection sits in that tree, so reopening the dropdown
  // lands on it rather than resetting to the top.
  const currentCoords = useMemo(() => {
    for (const [group, bases] of Object.entries(marketTree))
      for (const [baseKey, entry] of Object.entries(bases))
        for (const [period, label] of Object.entries(entry.periods))
          if (label === selectedMarketLabel) return { group, baseKey, period }
    return { group: null, baseKey: null, period: null }
  }, [marketTree, selectedMarketLabel])

  // With only ONE group there is nothing to choose, so it is selected for the
  // user and the Market column is populated straight away. Futures is the case
  // today — a league whose every market is an outright has no Player Props or
  // Game Markets to sit beside. Nulls stay nullish so an explicit override and
  // the current selection both still win.
  const soleMarketGroup = Object.keys(marketTree).length === 1 ? Object.keys(marketTree)[0] : null
  const activeMarketGroup = marketGroupOverride ?? currentCoords.group ?? soleMarketGroup
  const activeBaseKey     = baseKeyOverride ?? currentCoords.baseKey
  const activeBaseEntry   = activeMarketGroup ? marketTree[activeMarketGroup]?.[activeBaseKey] : null

  if (!isVisible) return null

  const togglePlayer = (key) => setExpandedPlayers(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  // Info bubble on the EXCHANGE filter group: why an exchange price here is
  // smaller than the one on the exchange's own screen.
  //
  // The dropdown it lives in is `overflow-y: auto`, which CLIPS any child that
  // overflows it - so the bubble cannot simply hang outside the panel. It is
  // kept NARROWER than the panel and grows back across it, which keeps the
  // whole thing inside the scroll box at any scroll position. Copy is in
  // config/exchanges.js and is deliberately two lines: this is a filter
  // panel, and the full explanation belongs on the education page.
  const ExchangeFeeInfo = () => {
    const [open, setOpen] = useState(false)
    return (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
          aria-label={EXCHANGE_FEE_NOTE.title}
          style={{
            width: '14px', height: '14px', borderRadius: '50%', cursor: 'pointer',
            border: '1px solid #35486b', backgroundColor: open ? '#35486b' : '#1b2438',
            color: '#94a3b8', fontSize: '9px', fontWeight: 700, lineHeight: 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          }}
        >i</button>
        {open && (
          <span
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: '19px', left: 0, zIndex: 400, width: '208px',
              padding: '8px 9px', textAlign: 'left', backgroundColor: '#111a2b',
              border: '1px solid #35486b', borderRadius: '6px',
              boxShadow: '0 6px 16px rgba(0,0,0,0.5)', cursor: 'default',
            }}
          >
            <span style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#f1f5f9', marginBottom: '4px' }}>
              {EXCHANGE_FEE_NOTE.title}
            </span>
            <span style={{ display: 'block', fontSize: '10px', lineHeight: 1.4, color: '#94a3b8', marginBottom: '6px' }}>
              {EXCHANGE_FEE_NOTE.body}
            </span>
            <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {EXCHANGE_FEE_NOTE.rates.map(r => (
                <span key={r.name} style={{
                  fontSize: '9.5px', fontWeight: 600, color: '#cbd5e1', padding: '1px 5px',
                  backgroundColor: '#1b2438', border: '1px solid #35486b', borderRadius: '3px',
                }}>{r.name} {r.label}</span>
              ))}
            </span>
          </span>
        )}
      </span>
    )
  }

  // Renders one odds <tr> (label cell + best-odds cell + per-book cells). Shared by the
  // flat market path and the grouped player-prop sub-rows.
  const renderOddsRow = (game, gameId, row, { label, labelCell, rowKey, labelStyle, blurRow = false, blurFromCol = Infinity, indent = false, showSubject = false } = {}) => {
    const bestPrice = bestOddsMap[`${gameId}_${row.sortKey}`]
    const bestBookies = bestPrice ? findAllBestBookies(game, row.sortKey, bestPrice, selectedBookmakers, selectedMarketKeys) : []
    const [firstBookie, ...extraBookies] = bestBookies
    const bestSource = firstBookie ? row.sources?.[firstBookie] : null
    const bestTrackable = !!bestPrice && !!bestSource && !blurRow && !isPreview
    const bestInSlip = bestTrackable && slipKeys.has(cellSlipKey(game, bestSource, firstBookie, bestPrice))
    const subjectOpp = showSubject ? flatRowSubject(row, game) : null
    return (
      <tr key={rowKey ?? row.sortKey} style={{ filter: blurRow ? 'blur(5px)' : 'none', pointerEvents: blurRow ? 'none' : 'auto', userSelect: blurRow ? 'none' : 'auto' }}>
        <td style={{ ...styles.oddsTdOutcome, ...(indent ? { paddingLeft: '28px', color: '#94a3b8' } : {}), ...(labelStyle || {}) }}>
          {/* A FLAT row carries its own thumbnail. Banded markets put the
              image on the player/team band instead, so this is off there -
              one picture per subject, not one per line. Resolved from the
              outcome (see flatRowSubject); a row that is about neither a
              person nor a club renders text only, exactly as before. */}
          {showSubject && subjectOpp ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span className="odds-band-subject">
                <SubjectImage opp={{ ...subjectOpp, sport: game.game.sport }} />
              </span>
              <span>{labelCell ?? label ?? row.label}</span>
            </span>
          ) : (labelCell ?? label ?? row.label)}
        </td>
        {/* The best-odds cell is a price like any other, and on a market with
            many empty book columns it is often the ONLY one in view — so it has
            to be selectable too, or a handicap looks unaddable. Adds it at the
            book whose logo it is showing; where several share the best price
            that is the first, which is the one named here. */}
        <td
          className={bestTrackable ? 'odds-track-cell' + (bestInSlip ? ' odds-track-cell--on' : '') : undefined}
          onClick={bestTrackable ? () => toggleOutcome(game, row, firstBookie, bestPrice) : undefined}
          style={{ ...styles.oddsTd, borderRight: '2px solid #1b2538', cursor: bestTrackable ? 'pointer' : 'default' }}
        >
          {bestPrice ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              {firstBookie && BOOKMAKER_LOGOS[firstBookie] && (
                <span className="odds-logo-wrap">
                  <img src={BOOKMAKER_LOGOS[firstBookie]} alt={firstBookie} title={firstBookie} style={{ width: '22px', height: '22px', borderRadius: '5px', objectFit: 'contain' }} />
                  {extraBookies.length > 0 && <>
                    <span className="odds-logo-badge">+{extraBookies.length}</span>
                    <span className="odds-logo-tooltip">
                      {extraBookies.map(bk => BOOKMAKER_LOGOS[bk]
                        ? <img key={bk} src={BOOKMAKER_LOGOS[bk]} alt={bk} title={bk} style={{ width: '18px', height: '18px', borderRadius: '4px', objectFit: 'contain' }} />
                        : <span key={bk} style={{ fontSize: '10px', color: '#94a3b8' }}>{bk}</span>
                      )}
                    </span>
                  </>}
                </span>
              )}
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#b0bec9' }}>{convertOdds(bestPrice, oddsFormat)}</span>
                {(() => {
                  const liq = formatLiquidity(bestSource?.outcome?.liquidity)
                  return liq ? <span style={{
                    position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                    marginTop: '1px', fontSize: '8.5px', fontWeight: 600, lineHeight: 1,
                    color: '#64748b', whiteSpace: 'nowrap', pointerEvents: 'none',
                  }}>{liq}</span> : null
                })()}
              </span>
            </div>
          ) : <span style={{ color: '#33455f' }}>—</span>}
        </td>
        {visibleBookmakers.map((bk, bkIdx) => {
          const price = row.prices[bk]
          const isBest = price && price === bestPrice
          const cellStyle = isBest ? styles.oddsTdBest : (price ? styles.oddsTd : styles.oddsTdMissing)
          const blurCell = blurRow ? false : bkIdx >= blurFromCol
          // A price is selectable; an empty cell is not, and neither is anything
          // behind the preview wall.
          const bkSource = row.sources?.[bk]
          const canTrack = !!price && !blurCell && !blurRow && !isPreview && !!bkSource
          // Already in the slip. Keyed through the SAME adapter the slip stores
          // from rather than rebuilding the string here, so the cell and the
          // slip cannot drift apart on markets whose display label is not the
          // selection (correct score being the obvious one).
          const inSlip = canTrack && slipKeys.has(cellSlipKey(game, bkSource, bk, price))
          return (
            <td
              key={bk}
              className={canTrack ? `odds-track-cell${inSlip ? ' odds-track-cell--on' : ''}` : undefined}
              onClick={canTrack ? () => toggleOutcome(game, row, bk, price) : undefined}
              style={{
                ...cellStyle,
                filter: blurCell ? 'blur(5px)' : 'none',
                userSelect: blurCell ? 'none' : 'auto',
                cursor: canTrack ? 'pointer' : 'default'
              }}
            >
              {price ? (() => {
                // Exchanges only: dollars resting at this price. The top of an
                // exchange book can be a few dollars deep, so the price alone
                // does not say whether it is takeable - see the EXCHANGE group
                // in config/bookmakers.js. Bookmakers send no liquidity and
                // render exactly as before.
                //
                // The price stays on the cell's own baseline and the figure is
                // absolutely positioned under it, inside the existing 11px of
                // bottom padding, so a row with liquidity is the SAME HEIGHT as
                // one without and the exchange columns do not stretch the table.
                const liq = formatLiquidity(bkSource?.outcome?.liquidity)
                return liq ? (
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    {convertOdds(price, oddsFormat)}
                    <span style={{
                      position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                      marginTop: '1px', fontSize: '8.5px', fontWeight: 600, lineHeight: 1,
                      color: '#64748b', whiteSpace: 'nowrap', pointerEvents: 'none',
                    }}>{liq}</span>
                  </span>
                ) : convertOdds(price, oddsFormat)
              })() : '—'}
            </td>
          )
        })}
      </tr>
    )
  }

  // Button label for the combined dropdown
  const dropdownLabel = selectedSport && selectedMarketLabel
    ? `${leagueLabel(selectedSport)} — ${selectedMarketLabel}`
    : selectedSport
      ? leagueLabel(selectedSport)
      : 'Sport / Market'

  return (
    <div style={styles.container} onClick={closeDropdowns}>
      <div>
        <style>{`
          .odds-page-title {
            font-size: 22px; font-weight: 600; margin: 0; color: #f1f5f9;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
            letter-spacing: -0.01em;
          }
          .odds-logo-wrap { position: relative; display: inline-flex; flex-shrink: 0; }

          /* Crests on the game header. 32px: the header's padding plus a 13px
             line is about 34px, so this is the largest the subject can be while
             still sitting IN the row rather than driving its height. Sizes are
             restated on the wrappers because SubjectImage ships the tracker's
             44px defaults — editing app.css does nothing to this page. */
          .odds-game-subject { display: inline-flex; flex-shrink: 0; line-height: 0; }
          .odds-game-subject .bc-lm-avatar-wrap,
          .odds-game-subject .bc-lm-logo-wrap,
          .odds-game-subject .bc-lm-logo-pair { width: 32px; height: 32px; transform: none; }
          .odds-game-subject .bc-lm-avatar-wrap { background: #1e2d45; border: none; box-shadow: none; backdrop-filter: none; -webkit-backdrop-filter: none; }
          .odds-game-subject .bc-lm-avatar-wrap::after { display: none; }
          /* Percentage + auto margins, never insets: an absolutely positioned
             <img> takes its INTRINSIC size when width is auto. */
          .odds-game-subject > .bc-lm-logo-wrap .bc-lm-logo-img { inset: 0; margin: auto; width: 88%; height: 88%; }
          /* The two members must satisfy  width + the second one's left = the
             slot width, or the pair spills past the slot and shunts the fixture
             name. Size therefore comes out of the GAP, which is the whole
             trade-off here: bigger crests can only be bought by hiding more of
             the one behind.
             It was 26 of 32 (a 20px, 77% overlap) and the two read as one
             smudge. Now 19 of 32: a 6px overlap, about 32%.
             *** THE PERCENTAGE UNDERSTATES IT ON A NON-ROUND CREST. ***
             .bc-lm-logo-img is object-fit: contain, so a WIDE mark (an MLB
             wordmark, most NFL logos) fits to the box WIDTH and fills only a
             thin horizontal band of it. Two of those at a nominal 50% overlap
             put the visible marks almost exactly on top of each other, which is
             why this needs to sit far lower than the round-crest leagues would
             suggest. AFL and soccer crests are roughly square and cope fine at
             the higher number; MLB is the case that sets the limit.
             The slot stays 32px, so nothing on the row shifts. The front one's
             drop-shadow still does the separating where they do touch.
             A tennis fixture is two FACES here, not two crests. */
          .odds-game-subject .bc-lm-logo-pair .bc-lm-logo-wrap { position: absolute; top: 7px; left: 0; width: 19px; height: 19px; }
          .odds-game-subject .bc-lm-logo-pair .bc-lm-logo-wrap:last-child { left: 13px; }
          .odds-game-subject .bc-ev-glyph { position: absolute; inset: 0; width: 100%; height: 100%; padding: 2px; box-sizing: border-box; color: #46566f; }
          .odds-game-subject .bc-ev-glyph-person { padding: 1px 1px 0; color: #3f4f66; }

          /* Player / team band. 28px, a touch smaller than the game header's 32,
             because the band is nested under it and the header should stay the
             larger of the two. */
          .odds-band-subject { display: inline-flex; flex-shrink: 0; line-height: 0; }
          .odds-band-subject .bc-lm-avatar-wrap,
          .odds-band-subject .bc-lm-logo-wrap { width: 28px; height: 28px; transform: none; }
          .odds-band-subject .bc-lm-avatar-wrap { background: #1e2d45; border: none; box-shadow: none; backdrop-filter: none; -webkit-backdrop-filter: none; }
          .odds-band-subject .bc-lm-avatar-wrap::after { display: none; }
          .odds-band-subject > .bc-lm-logo-wrap .bc-lm-logo-img { inset: 0; margin: auto; width: 88%; height: 88%; }
          .odds-band-subject .bc-ev-glyph { position: absolute; inset: 0; width: 100%; height: 100%; padding: 2px; box-sizing: border-box; color: #46566f; }
          .odds-band-subject .bc-ev-glyph-person { padding: 1px 1px 0; color: #3f4f66; }
          .odds-logo-badge {
            position: absolute; top: -4px; right: -5px;
            background: #0a1020; color: #8a8f96;
            font-size: 7px; font-weight: 700;
            border-radius: 4px; padding: 1px 3px; line-height: 1.3;
            pointer-events: none;
          }
          .odds-logo-tooltip {
            display: none; position: absolute; bottom: calc(100% + 8px); left: 50%;
            transform: translateX(-50%);
            background: #131d33; border: 1px solid #1b2538;
            border-radius: 6px; padding: 5px 7px; gap: 5px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.4);
            z-index: 200; flex-direction: row; align-items: center; white-space: nowrap;
          }
          .odds-logo-wrap:hover .odds-logo-badge { background: #3b4f68; color: #cbd5e1; }
          .odds-logo-wrap:hover .odds-logo-tooltip { display: flex; }
          .odds-table-scroll tbody tr:hover td { background-color: rgba(255,255,255,0.025); }
          .odds-table-scroll thead th { box-shadow: 0 1px 0 #1b2538, 0 4px 10px -6px rgba(0,0,0,0.6); }
          .odds-player-band {
            position: relative; transition: background 0.15s ease;
          }
          .odds-player-band:hover { background: #172642 !important; }
          .odds-chevron-pill {
            display: inline-flex; align-items: center; gap: 5px;
            font-size: 10.5px; font-weight: 700; color: #a78bfa;
            background: rgba(124,58,237,0.13); padding: 2px 8px; border-radius: 999px;
            letter-spacing: 0.02em;
          }
          .odds-table-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
          .odds-table-scroll::-webkit-scrollbar-track { background: transparent; }
          .odds-table-scroll::-webkit-scrollbar-thumb { background: #26354f; border-radius: 4px; }
          .odds-table-scroll::-webkit-scrollbar-thumb:hover { background: #35486b; }
          .odds-table-scroll::-webkit-scrollbar-corner { background: transparent; }
        `}</style>
        <div style={styles.content} className="tool-unified">
          <div style={styles.card}>

          {/* Header Row — breaks out of the card's padding to span full width */}
          <div className="bc-header" style={{ margin: '-16px -16px 0' }}>
            <div className="bc-header-titlewrap">
              <div className="bc-header-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
              <div className="bc-header-textcol">
                <div className="bc-header-titlerow">
                  <h1 className="bc-title">Sports Odds</h1>
                  <span
                    className={lastUpdate ? 'bc-live-dot bc-live-dot-green' : 'bc-live-dot bc-live-dot-grey'}
                    data-tooltip={lastUpdate ? `Last refreshed ${lastUpdate.toLocaleTimeString()}` : 'No data yet'}
                  />
                  {/* Sport / Market dropdown */}
                  <FilterDropdown
                    isOpen={openDropdown === 'sport'}
                    onToggle={() => toggleDropdown('sport')}
                    buttonContent={dropdownLabel}
                  >
                    <div style={{ display: 'flex', minWidth: '760px', maxHeight: '480px' }}>
                      {/* Sport — 8 categories. Deliberately the narrowest column:
                          it holds one short word per row and never scrolls. */}
                      <div className="odds-dd-scroll" style={{ width: '116px', flexShrink: 0, overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '4px 0 8px', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        <div style={styles.dropdownColumnLabel}>Sport</div>
                        {Object.keys(ODDS_HIERARCHY).map(category => (
                          <div
                            key={category}
                            onClick={() => {
                              setActiveCategory(category)
                              // Baseball, American Football and Hockey carry one
                              // league each, so the League column would offer a
                              // single row with no decision in it. Selecting the
                              // sport IS selecting the league. Free to do here —
                              // handleSportSelect does not fetch; only picking a
                              // market does.
                              const { leagues } = ODDS_HIERARCHY[category]
                              if (leagues.length === 1) handleSportSelect(leagues[0])
                            }}
                            className={`odds-dd-item${activeCategory === category ? ' selected' : ''}`}
                          >
                            {CATEGORY_SHORT_NAMES[category] || category}
                          </div>
                        ))}
                      </div>

                      {/* League — the active category's leagues only. */}
                      <div className="odds-dd-scroll" style={{ width: '150px', flexShrink: 0, overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '4px 0 8px', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        <div style={styles.dropdownColumnLabel}>League</div>
                        {activeCategory ? (
                          ODDS_HIERARCHY[activeCategory].leagues.map(id => (
                            <div key={id} onClick={() => handleSportSelect(id)} className={`odds-dd-item${selectedSport === id ? ' selected' : ''}`}>
                              {leagueLabel(id)}
                            </div>
                          ))
                        ) : (
                          <div style={{ color: '#475569', fontSize: '13px', padding: '12px 10px' }}>Select a sport</div>
                        )}
                      </div>

                      {/* Market GROUP — five rows, never scrolls. */}
                      <div className="odds-dd-scroll" style={{ width: '150px', flexShrink: 0, overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '4px 0 8px', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        <div style={styles.dropdownColumnLabel}>Group</div>
                        {selectedSport ? (
                          Object.keys(marketTree).map(group => (
                            <div
                              key={group}
                              onClick={() => { setMarketGroupOverride(group); setBaseKeyOverride(null) }}
                              className={`odds-dd-item${activeMarketGroup === group ? ' selected' : ''}`}
                            >
                              {group}
                            </div>
                          ))
                        ) : (
                          <div style={{ color: '#475569', fontSize: '13px', padding: '12px 10px' }}>Select a sport first</div>
                        )}
                      </div>

                      {/* Market — period variants collapsed into one row each. */}
                      <div className="odds-dd-scroll" style={{ width: '210px', flexShrink: 0, overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '4px 0 8px', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        <div style={styles.dropdownColumnLabel}>Market</div>
                        {activeMarketGroup ? (
                          Object.entries(marketTree[activeMarketGroup] || {}).map(([baseKey, entry]) => (
                            <div
                              key={baseKey}
                              onClick={() => {
                                setBaseKeyOverride(baseKey)
                                setMarketGroupOverride(activeMarketGroup)
                                // A market with only one period has no choice to
                                // offer, so skip the third column and commit now.
                                const periods = orderPeriods(entry.periods)
                                if (periods.length === 1) handleMarketSelect(entry.periods[periods[0]])
                              }}
                              className={`odds-dd-item${activeBaseKey === baseKey ? ' selected' : ''}`}
                            >
                              {entry.label}
                            </div>
                          ))
                        ) : (
                          <div style={{ color: '#475569', fontSize: '13px', padding: '12px 10px' }}>Select a group</div>
                        )}
                      </div>

                      {/* Period — only what this market is actually priced for.
                          Clicking one is what commits the selection and fetches. */}
                      <div className="odds-dd-scroll" style={{ flex: 1, minWidth: '130px', overflowY: 'auto', padding: '4px 0 8px', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        {/* "Period" means nothing for Top 5 or Without Nick Daicos -
                            futures fill this column with options, not periods. */}
                        <div style={styles.dropdownColumnLabel}>{isFuturesLeague(selectedSport) ? 'Option' : 'Period'}</div>
                        {activeBaseEntry ? (
                          orderPeriods(activeBaseEntry.periods).map(period => {
                            const label = activeBaseEntry.periods[period]
                            return (
                              <div
                                key={period}
                                onClick={() => handleMarketSelect(label)}
                                className={`odds-dd-item${selectedMarketLabel === label ? ' selected' : ''}`}
                              >
                                {period}
                              </div>
                            )
                          })
                        ) : (
                          <div style={{ color: '#475569', fontSize: '13px', padding: '12px 10px' }}>Select a market</div>
                        )}
                      </div>
                    </div>
                  </FilterDropdown>

                  {/* Games dropdown */}
                  {/* Shown whenever the tournament says more than the league row did:
                      several in view (a real filter), or a single one whose name
                      the league label does not already give you — "ATP" is showing
                      Cincinnati, "ATP Challenger" is showing Como.

                      The comparison keeps it off ordinary sports, where tournament
                      and league are the same string ("NRL" beside "NRL" tells
                      nobody anything), so no per-sport list has to be maintained.

                      With ONE tournament the button carries its NAME rather than the
                      word "Tournaments" — the label is the useful part, and the list
                      inside is then just confirmation. */}
                  {(allTournaments.length > 1 || (allTournaments.length === 1 && allTournaments[0] !== leagueLabel(selectedSport))) && (
                    <FilterDropdown
                      isOpen={openDropdown === 'tournament'}
                      onToggle={() => toggleDropdown('tournament')}
                      buttonContent={
                        allTournaments.length === 1
                          ? allTournaments[0]
                          : `Tournaments${hiddenTournaments.size ? ` (${allTournaments.length - hiddenTournaments.size}/${allTournaments.length})` : ''}`
                      }
                    >
                      <div className="odds-dd-scroll" style={{ width: '260px', maxHeight: '400px', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <button onClick={() => setHiddenTournaments(new Set())} className="odds-utility-btn">Select All</button>
                          <button onClick={() => setHiddenTournaments(new Set(allTournaments))} className="odds-utility-btn">Clear All</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          {allTournaments.map(t => (
                            <CheckboxItem key={t} item={t} selected={!hiddenTournaments.has(t)} onToggle={() => toggleTournament(t)} />
                          ))}
                        </div>
                      </div>
                    </FilterDropdown>
                  )}

                  {/* Futures has no games: each row group is a MARKET, and only one
                      market is selected at a time, so the filter would list a single
                      entry that cannot usefully be unticked. */}
                  {groupedGames.length > 0 && !isFuturesLeague(selectedSport) && (
                    <FilterDropdown
                      isOpen={openDropdown === 'game'}
                      onToggle={() => toggleDropdown('game')}
                      buttonContent={`Games${hiddenGames.size ? ` (${visibleGames.length}/${groupedGames.length})` : ''}`}
                    >
                      <div className="odds-dd-scroll" style={{ width: '300px', maxHeight: '440px', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        <input
                          autoFocus
                          value={gameSearch}
                          onChange={e => setGameSearch(e.target.value)}
                          placeholder="Search games…"
                          className="odds-search-input"
                        />
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <button onClick={() => setHiddenGames(new Set())} className="odds-utility-btn">Select All</button>
                          <button onClick={() => setHiddenGames(new Set(groupedGames.map(gameKey)))} className="odds-utility-btn">Clear All</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          {groupedGames
                            .filter(g => gameLabel(g).toLowerCase().includes(gameSearch.toLowerCase()))
                            .map(g => {
                              const key = gameKey(g)
                              return <CheckboxItem key={key} item={gameLabel(g)} selected={!hiddenGames.has(key)} onToggle={() => toggleGame(key)} />
                            })}
                        </div>
                      </div>
                    </FilterDropdown>
                  )}

                  {/* Round dropdown — only for markets split per round (Brownlow
                      3 Votes). One round at a time: the whole season is ~200
                      games. All rounds arrive in one request, so switching is a
                      client-side filter with no refetch. */}
                  {availableRounds.length > 0 && (
                    <FilterDropdown
                      isOpen={openDropdown === 'round'}
                      onToggle={() => toggleDropdown('round')}
                      buttonContent={effectiveRound != null ? roundLabel(effectiveRound) : 'Round'}
                    >
                      <div className="odds-dd-scroll" style={{ minWidth: '160px', maxHeight: '440px', overflowY: 'auto', padding: '4px 0 8px', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        {availableRounds.map(r => (
                          <div
                            key={r}
                            onClick={() => { setSelectedRound(r); toggleDropdown('round') }}
                            className={`odds-dd-item${effectiveRound === r ? ' selected' : ''}`}
                          >
                            {roundLabel(r)}
                          </div>
                        ))}
                      </div>
                    </FilterDropdown>
                  )}

                  {/* Players dropdown — only for player-prop markets */}
                  {/* Hidden where the players ARE the competitors (tennis): the Games
                      list already names both, one row per match instead of two. */}
                  {allPlayers.length > 0 && !playersAreCompetitors(selectedSport) && (
                    <FilterDropdown
                      isOpen={openDropdown === 'player'}
                      onToggle={() => toggleDropdown('player')}
                      buttonContent={`Players${hiddenPlayers.size ? ` (${allPlayers.length - hiddenPlayers.size}/${allPlayers.length})` : ''}`}
                    >
                      <div className="odds-dd-scroll" style={{ width: '300px', maxHeight: '440px', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                        <input
                          autoFocus
                          value={playerSearch}
                          onChange={e => setPlayerSearch(e.target.value)}
                          placeholder="Search players…"
                          className="odds-search-input"
                        />
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <button onClick={() => setHiddenPlayers(new Set())} className="odds-utility-btn">Select All</button>
                          <button onClick={() => setHiddenPlayers(new Set(allPlayers))} className="odds-utility-btn">Clear All</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          {allPlayers
                            .filter(p => p.toLowerCase().includes(playerSearch.toLowerCase()))
                            .map(p => (
                              <CheckboxItem key={p} item={p} selected={!hiddenPlayers.has(p)} onToggle={() => togglePlayerFilter(p)} />
                            ))}
                        </div>
                      </div>
                    </FilterDropdown>
                  )}

                  {/* Bookmakers dropdown */}
                  <FilterDropdown
                    isOpen={openDropdown === 'bookmaker'}
                    onToggle={() => toggleDropdown('bookmaker')}
                    buttonContent="Bookmakers"
                  >
                    <div className="odds-dd-scroll" style={{ width: '320px', maxHeight: '400px', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <button onClick={() => setSelectedBookmakers(new Set(ALL_BOOKMAKERS))} className="odds-utility-btn">Select All</button>
                        <button onClick={() => setSelectedBookmakers(new Set())} className="odds-utility-btn">Clear All</button>
                      </div>
                      {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
                        <div key={country} style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ padding: '4px 10px', backgroundColor: '#1b2438', border: '1px solid #35486b', color: '#f1f5f9', fontSize: '11px', borderRadius: '4px', fontWeight: '600', letterSpacing: '0.03em' }}>{country}</span>
                              {/* Exchange prices are shown net of the taker fee, which
                                  makes them LOWER here than on the exchange's own site.
                                  That needs saying where the group is named. */}
                              {country === 'EXCHANGE' && <ExchangeFeeInfo />}
                            </span>
                            <button onClick={() => toggleCountryBookmakers(country)} className="odds-utility-btn">
                              {bookmakers.every(bk => selectedBookmakers.has(bk)) ? 'Deselect' : 'Select'} All
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                            {bookmakers.map(bk => (
                              <CheckboxItem key={bk} item={bk} selected={selectedBookmakers.has(bk)} onToggle={toggleBookmakerSelection} logo={BOOKMAKER_LOGOS[bk]} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </FilterDropdown>
                </div>
              </div>
            </div>
            <div className="bc-header-actions">
              <button
                className={`bc-btn bc-btn-toggle${autoRefresh ? ' toggle-active' : ''}`}
                onClick={() => setAutoRefresh(p => !p)}
              >
                Auto
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
              <button
                className="bc-btn bc-icon-btn"
                onClick={() => fetchOdds(selectedSport, selectedMarketKeys)}
                disabled={loading || !selectedSport || !selectedMarketLabel}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
            </div>
          </div>

          {/* States — only shown when no data yet */}
          {error && !loading && groupedGames.length === 0 && (
            <div style={styles.stateBox}>
              <div style={styles.stateIcon}>⚠️</div>
              <div style={styles.stateText}>Error loading odds</div>
              <div style={styles.stateSubText}>{error}</div>
            </div>
          )}
          {!loading && !error && selectedMarketLabel && groupedGames.length === 0 && (
            <div style={styles.stateBox}>
              <div style={styles.stateIcon}>🔍</div>
              <div style={styles.stateText}>No upcoming games found</div>
              <div style={styles.stateSubText}>Try a different sport or market</div>
            </div>
          )}
          {/* Single connected table — bleeds to card edges */}
          {groupedGames.length > 0 && (
            <div style={{ position: 'relative', margin: '0 -16px -16px' }}>
              <div ref={tableWrapperRef} className="odds-table-scroll" style={{ overflow: isPreview ? 'hidden' : 'auto', height: isPreview ? 420 : tableHeight, borderTop: '1px solid #1b2538', borderRadius: '0 0 8px 8px', scrollbarWidth: 'thin', scrollbarColor: '#26354f transparent' }}>
                <table style={styles.oddsTable}>
                  <thead style={styles.oddsTableHead}>
                    <tr>
                      <th style={{ ...styles.oddsThOutcome, textAlign: 'center' }}>Outcome</th>
                      <th
                        style={{ ...styles.oddsThOutcome, minWidth: '80px', borderRight: '2px solid #1b2538', textAlign: 'center', cursor: oddsSortable ? 'pointer' : 'default', userSelect: 'none' }}
                        onClick={oddsSortable ? () => setBestSort(s => s === null ? 'desc' : s === 'desc' ? 'alpha' : null) : undefined}
                        title={oddsSortable ? 'Shortest odds first / longest first / A-Z' : undefined}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          Best
                          {oddsSortable && (
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: bestSort === 'alpha' ? 0.4 : 1 }}>
                              {bestSort === null
                                ? <polyline points="18 15 12 9 6 15"/>
                                : bestSort === 'desc'
                                  ? <polyline points="6 9 12 15 18 9"/>
                                  : <><polyline points="6 9 12 4 18 9"/><polyline points="6 15 12 20 18 15"/></>}
                            </svg>
                          )}
                        </span>
                      </th>
                      {visibleBookmakers.map(bk => (
                        <th key={bk} style={styles.oddsThBookmaker}>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {BOOKMAKER_LOGOS[bk] ? (
                              <img src={BOOKMAKER_LOGOS[bk]} alt={bk} title={bk} style={{ width: '28px', height: '28px', borderRadius: '8px', objectFit: 'contain' }} />
                            ) : (
                              <span style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{bk}</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleGames.map((game, idx) => {
                      if (isPreview && idx >= 3) return null
                      const outcomeRows = getOutcomeRows(game)
                      if (outcomeRows.length === 0) return null
                      const gameId = `${game.game.home_team}_${game.game.away_team}_${game.game.commence_time}`
                      const blurGame = isPreview && idx > 0
                      const lineGroups = getLineGroups(outcomeRows) || getSpreadGroup(outcomeRows)
                      return (
                        <React.Fragment key={idx}>
                          <tr style={{ filter: blurGame ? 'blur(5px)' : 'none', pointerEvents: blurGame ? 'none' : 'auto', userSelect: blurGame ? 'none' : 'auto' }}>
                            <td colSpan={2 + visibleBookmakers.length} style={{ padding: 0 }}>
                              <div style={styles.gameHeaderTd}>
                                {/* Crests go on the GAME HEADER, not the price
                                    rows: one image per fixture instead of one
                                    per outcome, on the line the eye uses to jump
                                    between games. A futures header has no two
                                    teams, so it falls back to the sport glyph. */}
                                <span className="odds-game-subject">
                                  <SubjectImage opp={{
                                    home_team: game.game.home_team,
                                    away_team: game.game.away_team,
                                    sport: game.game.sport
                                  }} />
                                </span>
                                {/* Futures name the MARKET here — there are no
                                    two teams and no start time to show. */}
                                <span style={styles.gameTitle}>
                                  {game.game.futuresTitle || `${game.game.away_team} vs ${game.game.home_team}`}
                                </span>
                                {game.game.commence_time && <>
                                  <span style={styles.gameSep}>·</span>
                                  <span style={styles.gameTime}>{formatTime(game.game.commence_time)}</span>
                                </>}
                              </div>
                            </td>
                          </tr>

                          {lineGroups ? (
                            // Over/Under markets (player props & totals): one collapsible block per
                            // group, main (consensus) line shown, remaining lines revealed on expand.
                            lineGroups.map((pg, pgIdx) => {
                              const pKey = `${gameId}_${pg.label}`
                              const expanded = expandedPlayers.has(pKey)
                              const mainLine = pg.lines.find(l => l.key === pg.mainKey) || pg.lines[0]
                              const otherLines = pg.lines.filter(l => l !== mainLine)
                              const blurRow = blurGame || (isPreview && idx === 0 && pgIdx >= 2)
                              const lineLabel = (row) => row.label.includes(' — ') ? row.label.split(' — ').slice(1).join(' — ') : row.label
                              return (
                                <React.Fragment key={pKey}>
                                  {/* Player name band */}
                                  <tr style={{ filter: blurRow ? 'blur(5px)' : 'none', pointerEvents: blurRow ? 'none' : 'auto' }}>
                                    <td colSpan={2 + visibleBookmakers.length}
                                        className="odds-player-band"
                                        style={{ padding: '9px 12px', background: '#19233490', borderTop: '1px solid #1b2538', borderBottom: '1px solid #1b2538', cursor: otherLines.length ? 'pointer' : 'default' }}
                                        onClick={() => otherLines.length && togglePlayer(pKey)}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {/* Only a band we can name gets an image.
                                            A player gets their headshot; a team
                                            total gets that team's crest, matched
                                            against the fixture so no bogus URL is
                                            ever requested. A game total ("Total
                                            Points") is neither, and gets nothing
                                            rather than an empty box. */}
                                        {(() => {
                                          const isPlayer = playerNameSet.has(pg.label)
                                          // A futures market has NO fixture, so there is no
                                          // home/away team to match a club band against -
                                          // futuresTeams carries the clubs that market prices
                                          // instead. Without it every crest on Brownlow Club
                                          // Total Votes was dropped.
                                          const isTeam = !isPlayer && (
                                            pg.label === game.game.home_team ||
                                            pg.label === game.game.away_team ||
                                            !!game.game.futuresTeams?.has(pg.label)
                                          )
                                          if (!isPlayer && !isTeam) return null
                                          return (
                                            <span className="odds-band-subject">
                                              <SubjectImage opp={{
                                                player_name: isPlayer ? pg.label : null,
                                                team_name: isTeam ? pg.label : null,
                                                sport: game.game.sport
                                              }} />
                                            </span>
                                          )
                                        })()}
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0', letterSpacing: '0.01em' }}>{pg.label}</span>
                                        {otherLines.length > 0 && (
                                          <span className="odds-chevron-pill">
                                            {pg.lines.length} lines
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                                                 style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
                                              <polyline points="6 9 12 15 18 9"/>
                                            </svg>
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                  {mainLine.rows.map(row => renderOddsRow(game, gameId, row, { label: lineLabel(row), blurRow, indent: true }))}
                                  {expanded && otherLines.map(l => l.rows.map(row => renderOddsRow(game, gameId, row, { label: lineLabel(row), blurRow, indent: true })))}
                                </React.Fragment>
                              )
                            })
                          ) : (
                            // Team / player markets (H2H, totals, spreads, tryscorers): flat rows.
                            // When the Best column sort is active, order this game's rows by best
                            // price (asc/desc); otherwise keep the default market/alpha order.
                            (oddsSortable && bestSort !== 'alpha'
                              ? [...outcomeRows].sort((a, b) => {
                                  // null = shortest price first (the default). Unpriced rows
                                  // sink either way, so they never sit among the favourites.
                                  const asc = bestSort === null
                                  const pa = bestOddsMap[`${gameId}_${a.sortKey}`] ?? Infinity
                                  const pb = bestOddsMap[`${gameId}_${b.sortKey}`] ?? Infinity
                                  if (pa === Infinity || pb === Infinity) return pa - pb
                                  return asc ? pa - pb : pb - pa
                                })
                              : outcomeRows
                            ).map((row, rowIdx) => {
                              const blurRow = blurGame || (isPreview && idx === 0 && rowIdx >= 2)
                              const blurFromCol = (isPreview && idx === 0 && rowIdx < 2) ? 3 : Infinity
                              // FLAT rows only: these have no player/team band
                              // above them, so the thumbnail goes on the row.
                              return renderOddsRow(game, gameId, row, { blurRow, blurFromCol, showSubject: true })
                            })
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Preview gradient fade */}
              {isPreview && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '120px', background: 'linear-gradient(to bottom, transparent, #010714)', pointerEvents: 'none', borderRadius: '0 0 8px 8px' }} />
              )}
            </div>
          )}

          </div>{/* end card */}

          {/* Preview CTA */}
          {isPreview && (
            <div style={{ padding: '28px 16px 36px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '520px' }}>
                <PreviewWall
                  title="Compare odds across every bookmaker right now"
                  features={['Best odds highlighted across all bookmakers', 'All sports, markets and alternate lines', 'Real time refresh', 'Much more...']}
                  planName="Premium"
                  accent="#60a5fa"
                  hadTrial={hadTrial}
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Selections gather here rather than opening a dialog per click. Singles
          and multis are the same list submitted differently, which is why the
          multi builder is not a separate feature on this page. */}
      <OddsBetslip
        legs={slipLegs}
        onRemove={removeFromSlip}
        onOddsChange={setLegOdds}
        onClear={() => setSlipLegs([])}
        onSaved={(mode, count) => setSlipToast(
          mode === 'multi' ? 'Multi added to tracker' : `${count} bet${count === 1 ? '' : 's'} added to tracker`
        )}
      />

      {slipToast && (
        <div
          onAnimationEnd={() => setSlipToast(null)}
          className="odds-slip-toast"
          style={{
            position: 'fixed', right: '24px', bottom: '24px', zIndex: 61,
            padding: '11px 16px', borderRadius: '10px',
            border: '1px solid rgba(34,211,238,0.4)', backgroundColor: '#0b1322',
            color: '#67e8f9', fontSize: '12.5px', fontWeight: 700
          }}
        >
          {slipToast}
        </div>
      )}
    </div>
  )
}
